/**
 * `createStore` — the typed IndexedDB facade.
 *
 * One {@link Store} owns one `IDBDatabase`. Every public method is async,
 * promise-based, and maps IndexedDB's callback API + bookkeeping
 * (`__version`/`__updatedAt`) into ergonomic typed CRUD with native cursor
 * streaming, optimistic concurrency, multi-store atomic transactions, optional
 * AES-GCM encryption, quota management, and full-DB export/import.
 */

import './error-codes'
import { makeCodec } from './codec'
import type { Codec } from './codec'
import { deriveKey } from './encryption'
import { reqToPromise, toKeyRange, txDone } from './idb'
import { openDatabase } from './migrations'
import { estimateQuota, isPersisted, requestPersistence } from './quota'
import { runTransaction } from './transaction'
import type { TxStore } from './transaction'
import {
	OfflineStoreError,
	type CreateStoreOptions,
	type CursorVisitor,
	type DatabaseExport,
	type IndexQuery,
	type InferRow,
	type QuotaEstimate,
	type Schema,
	type StoredRow,
} from './types'
import { invalidate } from './invalidation'
import { LIB_VERSION } from './version'

const DEFAULT_WATER_MARK = 0.8

/** Public, fully-typed store handle. `S` is the schema; table keys are `keyof S['tables']`. */
export class Store<S extends Schema> {
	private readonly db: IDBDatabase
	private readonly schema: S
	private readonly dbName: string
	private readonly waterMark: number
	private readonly keyPaths: Record<string, string>
	private closed = false

	/** @internal — use {@link createStore}. */
	constructor(
		db: IDBDatabase,
		schema: S,
		dbName: string,
		waterMark: number,
		key: CryptoKey | null,
	) {
		this.db = db
		this.schema = schema
		this.dbName = dbName
		this.waterMark = waterMark
		this.keyPaths = {}
		for (const [name, def] of Object.entries(schema.tables)) {
			this.keyPaths[name] = def.keyPath
		}
		// Stamp the db name onto a non-enumerable brand so the React
		// `useQuery` hook can build the invalidation-bus key without
		// reaching into private fields.
		Object.defineProperty(this, Symbol.for('norkron.web-offline-store.dbName'), {
			value: dbName,
			enumerable: false,
			configurable: true,
		})
		// Each table can have a different primary-key path; build a codec map
		// so encryption hoists the right key path out of the sealed envelope.
		const codecs: Record<string, Codec> = {}
		for (const [name, kp] of Object.entries(this.keyPaths)) {
			codecs[name] = makeCodec(kp, key)
		}
		this.codecs = codecs
	}

	private codecs: Record<string, Codec> = {}

	private codecFor(table: string): Codec {
		const c = this.codecs[table]
		if (!c) {
			throw new OfflineStoreError('invalid-input', `unknown table "${table}"`)
		}
		return c
	}

	private assertOpen(): void {
		if (this.closed) {
			throw new OfflineStoreError('idb-unavailable', `store "${this.dbName}" is closed`)
		}
	}

	private keyPathFor(table: string): string {
		const kp = this.keyPaths[table]
		if (!kp) throw new OfflineStoreError('invalid-input', `unknown table "${table}"`)
		return kp
	}

	/** Fetch one row by primary key, or `undefined` when absent. */
	async get<K extends keyof S['tables'] & string>(
		table: K,
		key: IDBValidKey,
	): Promise<StoredRow<InferRow<S, K>> | undefined> {
		this.assertOpen()
		const tx = this.db.transaction(table, 'readonly')
		const raw = await reqToPromise(tx.objectStore(table).get(key))
		if (raw === undefined) return undefined
		return (await this.codecFor(table).decode(raw)) as StoredRow<InferRow<S, K>>
	}

	/** Fetch every row in a table (decoded). Order is IndexedDB key order. */
	async getAll<K extends keyof S['tables'] & string>(
		table: K,
	): Promise<Array<StoredRow<InferRow<S, K>>>> {
		this.assertOpen()
		const tx = this.db.transaction(table, 'readonly')
		const raw = await reqToPromise(tx.objectStore(table).getAll())
		const decode = this.codecFor(table).decode
		return Promise.all(raw.map((r) => decode(r))) as Promise<
			Array<StoredRow<InferRow<S, K>>>
		>
	}

	/**
	 * Insert or replace a row. Stamps `__version` (incremented from any prior
	 * row) and `__updatedAt = Date.now()`. Returns the stored row.
	 */
	async put<K extends keyof S['tables'] & string>(
		table: K,
		value: InferRow<S, K>,
	): Promise<StoredRow<InferRow<S, K>>> {
		this.assertOpen()
		const kp = this.keyPathFor(table)
		const pk = (value as Record<string, unknown>)[kp] as IDBValidKey
		// Phase 1 — read the prior __version in its own readonly tx. The
		// bookkeeping columns are stored in clear text even when encryption is
		// on, so no decode is needed here.
		const rtx = this.db.transaction(table, 'readonly')
		const prior = (await reqToPromise(rtx.objectStore(table).get(pk))) as
			| { __version?: number }
			| undefined
		const stored = {
			...(value as Record<string, unknown>),
			__version: (prior?.__version ?? 0) + 1,
			__updatedAt: Date.now(),
		} as StoredRow<InferRow<S, K>>
		// Phase 2 — async encode (AES-GCM) OUTSIDE any open transaction so the
		// tx cannot auto-commit while crypto is in flight.
		const encoded = await this.codecFor(table).encode(stored)
		// Phase 3 — tight readwrite tx with no awaits before the request.
		const rwtx = this.db.transaction(table, 'readwrite')
		await reqToPromise(rwtx.objectStore(table).put(encoded))
		await txDone(rwtx)
		invalidate(this.dbName, table)
		return stored
	}

	/**
	 * Optimistic-concurrency update. Reads the current row, asserts its
	 * `__version === expectedVersion`, applies `mutator` to a *copy*, and
	 * writes back with `__version + 1`. Rejects with
	 * `OfflineStoreError(code="version-conflict")` if the stored version drifted
	 * or `code="not-found"` if the row is gone.
	 */
	async update<K extends keyof S['tables'] & string>(
		table: K,
		key: IDBValidKey,
		expectedVersion: number,
		mutator: (row: InferRow<S, K>) => InferRow<S, K> | void,
	): Promise<StoredRow<InferRow<S, K>>> {
		this.assertOpen()
		const codec = this.codecFor(table)
		// Phase 1 — read + decode + mutate + encode OUTSIDE any write tx so
		// async crypto never spans an open transaction.
		const rtx = this.db.transaction(table, 'readonly')
		const rawCurrent = await reqToPromise(rtx.objectStore(table).get(key))
		if (rawCurrent === undefined) {
			throw new OfflineStoreError('not-found', `row "${String(key)}" not found in "${table}"`)
		}
		const observedVersion = (rawCurrent as { __version?: number }).__version ?? 0
		if (observedVersion !== expectedVersion) {
			throw new OfflineStoreError(
				'version-conflict',
				`version conflict on "${table}"/"${String(key)}": expected ${expectedVersion}, stored ${observedVersion}`,
			)
		}
		const current = (await codec.decode(rawCurrent)) as StoredRow<InferRow<S, K>>
		const draft = structuredClone(current) as InferRow<S, K> & {
			__version: number
			__updatedAt: number
		}
		const returned = mutator(draft)
		const next = (returned ?? draft) as Record<string, unknown>
		const stored = {
			...next,
			__version: expectedVersion + 1,
			__updatedAt: Date.now(),
		} as StoredRow<InferRow<S, K>>
		const encoded = await codec.encode(stored)
		// Phase 2 — atomic compare-and-set: inside ONE readwrite tx, re-read the
		// clear-text __version and only write when it still equals
		// expectedVersion. No await between the get callback and the put, so a
		// concurrent writer cannot interleave (IndexedDB serialises tx).
		const rwtx = this.db.transaction(table, 'readwrite')
		const objStore = rwtx.objectStore(table)
		await new Promise<void>((resolve, reject) => {
			const getReq = objStore.get(key)
			getReq.onsuccess = () => {
				const fresh = getReq.result as { __version?: number } | undefined
				if (fresh === undefined) {
					reject(
						new OfflineStoreError('not-found', `row "${String(key)}" vanished mid-update`),
					)
					return
				}
				if ((fresh.__version ?? 0) !== expectedVersion) {
					reject(
						new OfflineStoreError(
							'version-conflict',
							`version conflict on "${table}"/"${String(key)}": expected ${expectedVersion}, stored ${
								fresh.__version ?? 0
							}`,
						),
					)
					return
				}
				const putReq = objStore.put(encoded)
				putReq.onsuccess = () => resolve()
				putReq.onerror = () =>
					reject(
						new OfflineStoreError('tx-aborted', `update put failed: ${putReq.error?.message}`, {
							cause: putReq.error,
						}),
					)
			}
			getReq.onerror = () =>
				reject(
					new OfflineStoreError('tx-aborted', `update get failed: ${getReq.error?.message}`, {
						cause: getReq.error,
					}),
				)
		}).catch((err) => {
			try {
				rwtx.abort()
			} catch {
				/* already aborting */
			}
			throw err
		})
		await txDone(rwtx)
		invalidate(this.dbName, table)
		return stored
	}

	/** Delete a row by primary key. No-op when the key is absent. */
	async delete<K extends keyof S['tables'] & string>(
		table: K,
		key: IDBValidKey,
	): Promise<void> {
		this.assertOpen()
		const rwtx = this.db.transaction(table, 'readwrite')
		await reqToPromise(rwtx.objectStore(table).delete(key))
		await txDone(rwtx)
		invalidate(this.dbName, table)
	}

	/** Count rows in a table. */
	async count<K extends keyof S['tables'] & string>(table: K): Promise<number> {
		this.assertOpen()
		const tx = this.db.transaction(table, 'readonly')
		return reqToPromise(tx.objectStore(table).count())
	}

	/** Empty a table. */
	async clear<K extends keyof S['tables'] & string>(table: K): Promise<void> {
		this.assertOpen()
		const rwtx = this.db.transaction(table, 'readwrite')
		await reqToPromise(rwtx.objectStore(table).clear())
		await txDone(rwtx)
		invalidate(this.dbName, table)
	}

	/**
	 * Query a named secondary index. Supports exact match (`equals`) or a
	 * range (`lower`/`upper` with open flags), `limit`, and `direction`.
	 */
	async queryByIndex<K extends keyof S['tables'] & string>(
		table: K,
		indexName: string,
		query: IndexQuery = {},
	): Promise<Array<StoredRow<InferRow<S, K>>>> {
		this.assertOpen()
		const tx = this.db.transaction(table, 'readonly')
		const index = tx.objectStore(table).index(indexName)
		const range = toKeyRange(query)
		const out: Array<StoredRow<InferRow<S, K>>> = []
		const codec = this.codecFor(table)
		const limit = query.limit ?? Number.POSITIVE_INFINITY
		// When encryption is off `decode` is synchronous, so collect raw rows
		// during the cursor walk and decode them after the tx closes — this
		// avoids a microtask hop between `cursor.continue()` calls that would
		// deactivate the transaction.
		const rawRows: unknown[] = []
		await new Promise<void>((resolve, reject) => {
			const cursorReq = index.openCursor(range ?? null, query.direction ?? 'next')
			cursorReq.onsuccess = () => {
				const cursor = cursorReq.result
				if (!cursor || rawRows.length >= limit) {
					resolve()
					return
				}
				rawRows.push(cursor.value)
				cursor.continue()
			}
			cursorReq.onerror = () =>
				reject(
					new OfflineStoreError('tx-aborted', `index cursor failed: ${cursorReq.error?.message}`, {
						cause: cursorReq.error,
					}),
				)
		})
		for (const r of rawRows.slice(0, limit)) {
			out.push((await Promise.resolve(codec.decode(r))) as StoredRow<InferRow<S, K>>)
		}
		return out
	}

	/**
	 * Stream rows via a native IndexedDB cursor without materialising the whole
	 * table. `visitor` is called per row; returning `false` stops iteration
	 * early. Optionally scoped to an index + range.
	 */
	async cursor<K extends keyof S['tables'] & string>(
		table: K,
		visitor: CursorVisitor<InferRow<S, K>>,
		opts?: { index?: string; query?: IndexQuery },
	): Promise<void> {
		this.assertOpen()
		const tx = this.db.transaction(table, 'readonly')
		const objStore = tx.objectStore(table)
		const source: IDBObjectStore | IDBIndex = opts?.index
			? objStore.index(opts.index)
			: objStore
		const range = toKeyRange(opts?.query ?? {})
		const codec = this.codecFor(table)
		// Buffer raw rows during the native cursor walk (synchronous, keeps the
		// tx active), then decode + dispatch to the visitor after the tx
		// closes. Early-stop is honoured: a visitor returning `false` halts
		// dispatch immediately.
		const rawRows: unknown[] = []
		await new Promise<void>((resolve, reject) => {
			const cursorReq = source.openCursor(
				range ?? null,
				opts?.query?.direction ?? 'next',
			)
			cursorReq.onsuccess = () => {
				const cursor = cursorReq.result
				if (!cursor) {
					resolve()
					return
				}
				rawRows.push(cursor.value)
				cursor.continue()
			}
			cursorReq.onerror = () =>
				reject(
					new OfflineStoreError('tx-aborted', `cursor failed: ${cursorReq.error?.message}`, {
						cause: cursorReq.error,
					}),
				)
		})
		for (const r of rawRows) {
			const row = (await Promise.resolve(codec.decode(r))) as StoredRow<InferRow<S, K>>
			const verdict = await Promise.resolve(visitor(row))
			if (verdict === false) return
		}
	}

	/**
	 * Run a multi-store atomic transaction. The callback receives a
	 * `{ tableName: { put, get, delete } }` scope bound to one `readwrite`
	 * transaction. Resolving commits; throwing rolls everything back.
	 *
	 * `put` inside a tx auto-bumps `__version` (read-from-current + 1) and
	 * stamps `__updatedAt`, exactly like the top-level {@link put}, so the
	 * sync-queue outbox sees consistent change metadata regardless of which
	 * write path produced the row.
	 */
	async tx<R>(
		tables: ReadonlyArray<keyof S['tables'] & string>,
		fn: (scope: {
			[K in keyof S['tables'] & string]: TxStore<StoredRow<InferRow<S, K>>>
		}) => Promise<R> | R,
	): Promise<R> {
		this.assertOpen()
		const keyPaths = this.keyPaths
		const result = await runTransaction(
			this.db,
			tables,
			(name) => this.codecFor(name),
			async (rawScope) => {
				const typed: Record<string, TxStore<unknown>> = {}
				for (const name of tables) {
					const base = rawScope[name]
					if (!base) {
						throw new OfflineStoreError('invalid-input', `table "${name}" missing from tx scope`)
					}
					const kp = keyPaths[name] as string
					typed[name] = {
						put: async (value: unknown) => {
							const v = value as Record<string, unknown>
							const pk = v[kp] as IDBValidKey
							const prior = (await base.get(pk)) as { __version?: number } | undefined
							await base.put({
								...v,
								__version: (prior?.__version ?? 0) + 1,
								__updatedAt: Date.now(),
							})
						},
						get: (k: IDBValidKey) => base.get(k),
						delete: (k: IDBValidKey) => base.delete(k),
					}
				}
				return fn(
					typed as unknown as {
						[K in keyof S['tables'] & string]: TxStore<StoredRow<InferRow<S, K>>>
					},
				)
			},
		)
		for (const t of tables) invalidate(this.dbName, t)
		return result
	}

	/** Request durable storage. Resolves to whether persistence was granted. */
	persist(): Promise<boolean> {
		return requestPersistence()
	}

	/** Whether storage is already durable. */
	persisted(): Promise<boolean> {
		return isPersisted()
	}

	/** Best-effort quota estimate; emits `WEB_OFFLINE_STORE_QUOTA_LOW` at the mark. */
	estimate(): Promise<QuotaEstimate> {
		return estimateQuota(this.waterMark)
	}

	/**
	 * @internal — read the *raw* on-disk record (codec NOT applied). Used by
	 * the test suite to assert that encrypted payloads are sealed at rest.
	 * Not part of the public contract; do not rely on this in app code.
	 */
	async __rawRecord(table: string, key: IDBValidKey): Promise<unknown> {
		this.assertOpen()
		const tx = this.db.transaction(table, 'readonly')
		return reqToPromise(tx.objectStore(table).get(key))
	}

	/** Serialize every table (decoded rows) into a portable JSON envelope. */
	async exportAll(): Promise<DatabaseExport> {
		this.assertOpen()
		const tables: Record<string, ReadonlyArray<unknown>> = {}
		for (const name of Object.keys(this.schema.tables)) {
			const tx = this.db.transaction(name, 'readonly')
			const raw = await reqToPromise(tx.objectStore(name).getAll())
			const decode = this.codecFor(name).decode
			tables[name] = await Promise.all(raw.map((r) => decode(r)))
		}
		return {
			lib: '@norkron/web-offline-store',
			libVersion: LIB_VERSION,
			dbName: this.dbName,
			schemaVersion: this.schema.version,
			exportedAt: Date.now(),
			tables,
		}
	}

	/**
	 * Replace the contents of every table present in `dump.tables`. Each table
	 * is cleared then repopulated inside one atomic transaction per table.
	 * Rows are re-encoded so an encrypted store stays encrypted on import.
	 */
	async importAll(dump: DatabaseExport): Promise<void> {
		this.assertOpen()
		for (const [name, rows] of Object.entries(dump.tables)) {
			if (!(name in this.schema.tables)) continue
			const codec = this.codecFor(name)
			// Encode every row OUTSIDE the write tx (async AES must not span an
			// open transaction). Then clear + repopulate in one tight tx.
			const encoded: unknown[] = []
			for (const row of rows) {
				encoded.push(await Promise.resolve(codec.encode(row)))
			}
			const rwtx = this.db.transaction(name, 'readwrite')
			const objStore = rwtx.objectStore(name)
			await new Promise<void>((resolve, reject) => {
				const clearReq = objStore.clear()
				clearReq.onsuccess = () => {
					let pending = encoded.length
					if (pending === 0) {
						resolve()
						return
					}
					for (const e of encoded) {
						const putReq = objStore.put(e)
						putReq.onsuccess = () => {
							if (--pending === 0) resolve()
						}
						putReq.onerror = () =>
							reject(
								new OfflineStoreError('tx-aborted', `import put failed: ${putReq.error?.message}`, {
									cause: putReq.error,
								}),
							)
					}
				}
				clearReq.onerror = () =>
					reject(
						new OfflineStoreError('tx-aborted', `import clear failed: ${clearReq.error?.message}`, {
							cause: clearReq.error,
						}),
					)
			})
			await txDone(rwtx)
			invalidate(this.dbName, name)
		}
	}

	/** Close the underlying database connection. Idempotent. */
	close(): void {
		if (this.closed) return
		this.closed = true
		this.db.close()
	}
}

/**
 * Open (creating/migrating as needed) and return a typed {@link Store}.
 *
 * @example
 * ```ts
 * const store = await createStore({
 *   dbName: 'app',
 *   schema: defineSchema({ users: table<User>('id').withIndex('byEmail','email') }, 1),
 * })
 * await store.put('users', { id: 'u1', email: 'a@b.c' })
 * ```
 */
export async function createStore<S extends Schema>(
	options: CreateStoreOptions & { schema: S },
): Promise<Store<S>> {
	const { dbName, schema, migrations = [], encryption, quotaLowWaterMark } = options
	const db = await openDatabase(dbName, schema, migrations)
	const key = encryption ? await deriveKey(encryption, dbName) : null
	return new Store<S>(
		db,
		schema,
		dbName,
		quotaLowWaterMark ?? DEFAULT_WATER_MARK,
		key,
	)
}
